import {
  CopyPublicLinkButton,
  PageHeaderActionSecondary,
  PageHeaderActions,
  ButtonDropdownChevron,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
  navigateWithThreadPreservation,
  copyPublicUrl,
  canReapplyAfterWithdraw,
  formatApplicationStatus,
  isActiveApplicationStatus,
  isBlockedApplicationStatus,
  showsCandidateSavedBookmark,
} from '@yeshuman/sdk'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Check,
  Link2,
  Loader2,
  LogOut,
  MoreHorizontal,
  Send,
  Bookmark,
} from 'lucide-react'

import { JobBoardSaveButton } from './job-board-save-button'
import { WithdrawApplicationDialog } from './withdraw-application-dialog'
import { useWithdrawApplication } from '../../hooks/use-withdraw-application'

export type CandidateJobApplication = {
  id: string
  status: string
  applied_at: string
}

type CandidateJobDetailHeaderActionsProps = {
  shareUrl: string
  application: CandidateJobApplication | null
  saved: boolean
  isSaving?: boolean
  onSaveToggle: () => void
  onApply: () => void
  onWithdrawn: () => void
  busy?: boolean
}

export function CandidateJobDetailHeaderActions({
  shareUrl,
  application,
  saved,
  isSaving = false,
  onSaveToggle,
  onApply,
  onWithdrawn,
  busy = false,
}: CandidateJobDetailHeaderActionsProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const withdraw = useWithdrawApplication({
    applicationId: application?.id,
    onWithdrawn,
  })

  const actionBusy = busy || withdraw.isPending
  const status = application?.status ?? null
  const canWithdraw = status != null && isActiveApplicationStatus(status)
  const canReapply = status != null && canReapplyAfterWithdraw(status)
  const isBlocked = status != null && isBlockedApplicationStatus(status)
  const showApplicationHistory = application != null && (canReapply || isBlocked)
  const showBookmark = showsCandidateSavedBookmark(application)

  const openApplicationDetail = () => {
    if (!application?.id) return
    navigateWithThreadPreservation(
      navigate,
      `/candidate/jobs?tab=applied&application=${encodeURIComponent(application.id)}`,
      searchParams,
    )
  }

  const saveButton = showBookmark ? (
    <JobBoardSaveButton
      saved={saved}
      disabled={actionBusy || isSaving}
      onClick={onSaveToggle}
    />
  ) : null

  const widePrimary = (() => {
    if (!application || canReapply) {
      return (
        <Button type="button" variant="default" size="sm" disabled={actionBusy} onClick={onApply}>
          <Send className="h-4 w-4" aria-hidden />
          {canReapply ? 'Apply again' : 'Apply'}
        </Button>
      )
    }
    if (canWithdraw) {
      return (
        <div className="inline-flex items-stretch">
          <Button type="button" variant="successMuted" size="sm" disabled splitLead>
            <Check className="h-4 w-4" aria-hidden />
            Applied
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="successMuted"
                size="sm"
                disabled={actionBusy}
                splitChevron
                aria-label="More application actions"
              >
                <ButtonDropdownChevron />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                className="gap-2 text-destructive focus:text-destructive"
                disabled={actionBusy}
                onClick={withdraw.openDialog}
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Withdraw
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }
    const label = formatApplicationStatus(status!)
    return (
      <Button type="button" variant="secondary" size="sm" disabled>
        {label}
      </Button>
    )
  })()

  const overflowPrimaryItems = (() => {
    if (!application || canReapply) {
      return (
        <DropdownMenuItem className="gap-2" disabled={actionBusy} onClick={onApply}>
          <Send className="h-4 w-4" aria-hidden />
          {canReapply ? 'Apply again' : 'Apply'}
        </DropdownMenuItem>
      )
    }
    if (canWithdraw) {
      return (
        <>
          <DropdownMenuItem disabled className="gap-2">
            <Check className="h-4 w-4" aria-hidden />
            Applied
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2 text-destructive focus:text-destructive"
            disabled={actionBusy}
            onClick={withdraw.openDialog}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Withdraw
          </DropdownMenuItem>
        </>
      )
    }
    return (
      <DropdownMenuItem disabled className="gap-2">
        {formatApplicationStatus(status!)}
      </DropdownMenuItem>
    )
  })()

  return (
    <>
      <div className="hidden @min-[480px]:block">
        <PageHeaderActions
          tertiary={
            <>
              <CopyPublicLinkButton url={shareUrl} iconOnly />
              {saveButton}
            </>
          }
          secondary={
            showApplicationHistory ? (
              <PageHeaderActionSecondary onClick={openApplicationDetail}>
                View application
              </PageHeaderActionSecondary>
            ) : null
          }
          primary={widePrimary}
        />
      </div>

      <div className="@min-[480px]:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghostToolbar"
              size="iconSm"
              disabled={actionBusy}
              aria-label="Job actions"
            >
              {actionBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <MoreHorizontal className="h-4 w-4" aria-hidden />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              className="gap-2"
              disabled={actionBusy}
              onClick={() => void copyPublicUrl(shareUrl)}
            >
              <Link2 className="h-4 w-4" aria-hidden />
              Copy link
            </DropdownMenuItem>
            {showBookmark ? (
              <DropdownMenuItem
                className="gap-2"
                disabled={actionBusy || isSaving}
                onClick={onSaveToggle}
              >
                <Bookmark className={cn('h-4 w-4', saved && 'fill-current')} aria-hidden />
                {saved ? 'Remove' : 'Save'}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            {overflowPrimaryItems}
            {showApplicationHistory ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2" onClick={openApplicationDetail}>
                  View application
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <WithdrawApplicationDialog {...withdraw.dialogProps} />
    </>
  )
}
